import * as moment from 'moment';
import { Component, ElementRef, OnInit } from '@angular/core';
import { ActivatedRoute, Router } from '@angular/router';
import { PageBase, LocationInfo, LocationGroupInfo, QueuedMessageAudienceConditionInfo, QueuedMessageConditionField, QueuedMessageConditionGroup, QueuedMessageConditionOperator, QueuedMessagePriority, QueuedMessageSettingInfo, QueuedMessageCategory, QueuedMessageMethod, GroupLocationHierarchyInfo } from 't4core';
import { T4ConditionEditorFieldOption, T4ConditionEditorOption } from '../../../../t4common/components/t4-condition-editor/t4-condition-editor.component';

@Component({
  selector: 'app-create-notification-setting',
  templateUrl: './create-notification-setting.html',
  styleUrls: ['./create-notification-setting.css']
})
export class CreateNotificationSettingComponent extends PageBase implements OnInit {
  public isLoading: boolean = false;
  public isSaving: boolean = false;
  public notificationId: string;

  public setting: QueuedMessageSettingInfo = null;

  public fields: T4ConditionEditorFieldOption[] = [];
  public operators: T4ConditionEditorOption[] = [];
  private locationGroups: LocationGroupInfo[] = [];
  private locations: LocationInfo[] = [];
  private conceptOptions: T4ConditionEditorOption[] = [];
  private groupOptions: T4ConditionEditorOption[] = [];
  private locationOptions: T4ConditionEditorOption[] = [];

  public affectedUserCount: number = null;
  public isLoadingAudienceCount: boolean = false;
  public isAudienceCountOutdated: boolean = false;
  public scheduledDateSelected: boolean = false;
  public scheduledTime: string = '';

  public priorities = [
    { Id: QueuedMessagePriority.Low, Name: this.translate('Turbo.Notifications.QueuedMessagePriority.Low', 'Low') },
    { Id: QueuedMessagePriority.Normal, Name: this.translate('Turbo.Notifications.QueuedMessagePriority.Normal', 'Normal') },
    { Id: QueuedMessagePriority.High, Name: this.translate('Turbo.Notifications.QueuedMessagePriority.High', 'High') }
  ];

  public methods = [
    { Id: QueuedMessageMethod.Notification, Name: this.translate('Turbo.Notifications.QueuedMessageMethod.Notification', 'App notification') },
    { Id: QueuedMessageMethod.Sms, Name: this.translate('Turbo.Notifications.QueuedMessageMethod.Sms', 'Sms') },
    { Id: QueuedMessageMethod.Email, Name: this.translate('Turbo.Notifications.QueuedMessageMethod.Email', 'Email') }
  ];

  public categories = [
    { Id: QueuedMessageCategory.CustomerConfirmation, Name: this.translate('Turbo.Notifications.Category.CustomerConfirmation', 'Customer confirmation') },
    { Id: QueuedMessageCategory.InternalNotification, Name: this.translate('Turbo.Notifications.Category.InternalNotification', 'Internal notification') },
    { Id: QueuedMessageCategory.Marketing, Name: this.translate('Turbo.Notifications.Category.Marketing', 'Marketing') },
    { Id: QueuedMessageCategory.Reminder, Name: this.translate('Turbo.Notifications.Category.Reminder', 'Reminder') },
    { Id: QueuedMessageCategory.CustomerService, Name: this.translate('Turbo.Notifications.Category.CustomerService', 'Customer service') }
  ];

  constructor(private el: ElementRef, private route: ActivatedRoute, private router: Router) {
    super(el);
  }

  public async ngOnInit() {
    this.isLoading = true;

    try {
      await this.loadConditionDropdownOptions();
      const [fields, operators] = await Promise.all([this.getConditionFields(), this.getConditionOperators()]);
      this.fields = fields;
      this.operators = operators;

      const id = this.route.snapshot.paramMap.get('id');

      if (!id || id === 'create') {
        this.setting = this.createEmptySetting();
        return;
      }

      this.notificationId = id;
      this.setting = await this.Api.get<QueuedMessageSettingInfo>("/Notification/GetNotificationSetting", { id: this.notificationId });
      if (!this.setting) {
        this.setting = this.createEmptySetting();
      }
      this.setting.Conditions ??= [];
      this.setting.MaximumSendCount = this.setting.MaximumSendCount || 1;

      if (this.setting.ScheduledAt) {
        this.scheduledDateSelected = true;
        this.scheduledTime = this.setting.ScheduledAt.format('HH:mm');
      }
    } finally {
      this.isLoading = false;
    }
  }

  public onConditionsChanged(conditions: QueuedMessageAudienceConditionInfo[]) {
    this.setting.Conditions = conditions ?? [];
    if (this.affectedUserCount !== null) {
      this.isAudienceCountOutdated = true;
    }
  }

  public canCheckAffectedUsers(): boolean {
    const conditions = this.setting?.Conditions ?? [];
    return conditions.length > 0 && conditions.every(condition => {
      const hasValue = condition.Value !== null && condition.Value !== undefined && condition.Value.toString().trim() !== '';
      if (!hasValue) {
        return false;
      }
      if (condition.Operator === QueuedMessageConditionOperator.Between) {
        return condition.Value2 !== null && condition.Value2 !== undefined && condition.Value2.toString().trim() !== '';
      }
      return true;
    });
  }

  public async checkAffectedUsers(): Promise<void> {
    if (!this.setting) {
      this.affectedUserCount = 0;
      return;
    }

    const validConditions = (this.setting.Conditions ?? []).filter(condition => {
      const hasValue = condition.Value !== null && condition.Value !== undefined && condition.Value.toString().trim() !== '';
      if (!hasValue) {
        return false;
      }

      if (condition.Operator === QueuedMessageConditionOperator.Between) {
        return condition.Value2 !== null && condition.Value2 !== undefined && condition.Value2.toString().trim() !== '';
      }

      return true;
    });

    if (validConditions.length === 0) {
      this.affectedUserCount = 0;
      this.isAudienceCountOutdated = false;
      return;
    }

    this.isLoadingAudienceCount = true;
    try {
      this.affectedUserCount = await this.Api.post<number>("/Notification/GetNotificationAudienceCount", validConditions, { category: this.setting.Category }) ?? 0;
      this.isAudienceCountOutdated = false;
    } finally {
      this.isLoadingAudienceCount = false;
    }
  }

  onScheduledDateChange(date: moment.Moment): void {
    this.scheduledDateSelected = !!date;

    if (!date) {
      this.setting.ScheduledAt = undefined;
      return;
    }

    const scheduledAt = this.setting.ScheduledAt ? this.setting.ScheduledAt.clone() : date.clone().startOf('day');
    scheduledAt.year(date.year()).month(date.month()).date(date.date());
    this.setting.ScheduledAt = scheduledAt;
  }

  onScheduleTypeChanged(): void {
    if (this.setting.IsOneTime) {
      this.setting.MaximumSendCount = 1;
      this.setting.RepeatIntervalInMinutes = 0;
    }
  }

  onScheduledTimeChange(time: string): void {
    this.scheduledTime = time;
    if (!time) {
      return;
    }

    const [hours, minutes] = time.split(':').map(Number);
    const scheduledAt = this.setting.ScheduledAt ? this.setting.ScheduledAt.clone() : moment();
    scheduledAt.hour(hours).minute(minutes).second(0).millisecond(0);
    this.setting.ScheduledAt = scheduledAt;
  }

  private async loadConditionDropdownOptions(): Promise<void> {
    const hierarchy = await this.Api.get<GroupLocationHierarchyInfo>('Location/GetGroupLocationHierarchy', {});

    this.locationGroups = hierarchy?.Concepts ?? [];
    this.locations = hierarchy?.Locations ?? [];
    this.conceptOptions = this.createConceptOptions();

    if (this.conceptOptions.length > 0) {
      this.groupOptions = this.createConceptGroupOptions();
    } else {
      this.groupOptions = this.createDirectGroupOptions(hierarchy?.Groups ?? []);
    }

    this.locationOptions = this.createLocationOptions();
  }

  private createConceptOptions(): T4ConditionEditorOption[] {
    return this.locationGroups.map(concept => ({ Id: concept.Id, Name: concept.Name, Value: concept.ContextId }));
  }

  private createConceptGroupOptions(): T4ConditionEditorOption[] {
    const options: T4ConditionEditorOption[] = [];
    const existingKeys = new Set<string>();
    for (const concept of this.locationGroups) {
      for (const group of concept.ChildGroups ?? []) {
        const key = `${concept.Id}_${group.Id}`;
        if (existingKeys.has(key)) {
          continue;
        }
        existingKeys.add(key);
        options.push({ Id: group.Id, Name: group.Name, Value: group.ContextId, ParentId: concept.Id });
      }
    }
    return options;
  }

  private createDirectGroupOptions(groups: LocationGroupInfo[]): T4ConditionEditorOption[] {
    return groups.map(group => ({ Id: group.Id, Name: group.Name, Value: group.ContextId }));
  }

  private createLocationOptions(): T4ConditionEditorOption[] {
    return this.locations.map(location => {
      const groupIds = this.parseHierarchy(location.Hierarchy);
      const conceptIds = Array.from(new Set(this.groupOptions.filter(group => groupIds.includes(group.Id)).map(group => group.ParentId).filter((conceptId): conceptId is number => conceptId !== undefined)));
      return { Id: location.Id, Name: location.Name, Value: location.ContextId, ParentIds: groupIds, ConceptIds: conceptIds };
    });
  }

  private parseHierarchy(hierarchy?: string): number[] {
    if (!hierarchy) {
      return [];
    }
    return hierarchy.split('-').map(value => Number(value)).filter(value => Number.isFinite(value) && value > 0);
  }

  private async getConditionFields(): Promise<T4ConditionEditorFieldOption[]> {
    const countries = this.getCountryOptions();
    const rentalStatuses = this.getRentalStatusOptions();
    const [customerName, customerEmail, customerCountry, customerPhone, rentalId, rentalStatus, rentalCreatedAt, rentalStartDate, rentalEndDate, concept, group, location, locationCountry] = await Promise.all([
      this.translate('Turbo.Notifications.QueuedMessageConditionField.CustomerName', 'Customer > Name'),
      this.translate('Turbo.Notifications.QueuedMessageConditionField.CustomerEmail', 'Customer > Email'),
      this.translate('Turbo.Notifications.QueuedMessageConditionField.CustomerCountry', 'Customer > Country'),
      this.translate('Turbo.Notifications.QueuedMessageConditionField.CustomerPhone', 'Customer > Phone'),
      this.translate('Turbo.Notifications.QueuedMessageConditionField.RentalId', 'Rental > Id'),
      this.translate('Turbo.Notifications.QueuedMessageConditionField.RentalStatus', 'Rental > Status'),
      this.translate('Turbo.Notifications.QueuedMessageConditionField.RentalCreatedAt', 'Rental > Created at'),
      this.translate('Turbo.Notifications.QueuedMessageConditionField.RentalStartDate', 'Rental > Start date'),
      this.translate('Turbo.Notifications.QueuedMessageConditionField.RentalEndDate', 'Rental > End date'),
      this.translate('Turbo.Notifications.QueuedMessageConditionField.Concept', 'Concept'),
      this.translate('Turbo.Notifications.QueuedMessageConditionField.Group', 'Group'),
      this.translate('Turbo.Notifications.QueuedMessageConditionField.Location', 'Location'),
      this.translate('Turbo.Notifications.QueuedMessageConditionField.LocationCountry', 'Location > Country')
    ]);

    const fields: T4ConditionEditorFieldOption[] = [
      {
        Id: QueuedMessageConditionField.CustomerName,
        Name: customerName,
        ValueType: 'text'
      },
      {
        Id: QueuedMessageConditionField.CustomerEmail,
        Name: customerEmail,
        ValueType: 'text'
      },
      {
        Id: QueuedMessageConditionField.CustomerCountry,
        Name: customerCountry,
        ValueType: 'select',
        Values: countries
      },
      {
        Id: QueuedMessageConditionField.CustomerPhone,
        Name: customerPhone,
        ValueType: 'text'
      },
      {
        Id: QueuedMessageConditionField.RentalId,
        Name: rentalId,
        ValueType: 'number'
      },
      {
        Id: QueuedMessageConditionField.RentalStatus,
        Name: rentalStatus,
        ValueType: 'select',
        Values: rentalStatuses
      },
      {
        Id: QueuedMessageConditionField.RentalCreatedAt,
        Name: rentalCreatedAt,
        ValueType: 'date'
      },
      {
        Id: QueuedMessageConditionField.RentalStartDate,
        Name: rentalStartDate,
        ValueType: 'date'
      },
      {
        Id: QueuedMessageConditionField.RentalEndDate,
        Name: rentalEndDate,
        ValueType: 'date'
      }
    ];

    if (this.conceptOptions.length > 0) {
      fields.push({
        Id: QueuedMessageConditionField.Concept,
        Name: concept,
        ValueType: 'select',
        Values: this.conceptOptions
      });
    }

    if (this.groupOptions.length > 0) {
      const dependencies: any[] = [];

      if (this.conceptOptions.length > 0) {
        dependencies.push({
          FieldId: QueuedMessageConditionField.Concept,
          FilterMode: 'parentId'
        });
      }

      fields.push({
        Id: QueuedMessageConditionField.Group,
        Name: group,
        ValueType: 'select',
        Values: this.groupOptions,
        Dependencies: dependencies
      });
    }

    if (this.locationOptions.length > 0) {
      const dependencies: any[] = [];

      if (this.conceptOptions.length > 0) {
        dependencies.push({
          FieldId: QueuedMessageConditionField.Concept,
          FilterMode: 'conceptIds'
        });
      }

      if (this.groupOptions.length > 0) {
        dependencies.push({
          FieldId: QueuedMessageConditionField.Group,
          FilterMode: 'parentIds'
        });
      }

      fields.push({
        Id: QueuedMessageConditionField.Location,
        Name: location,
        ValueType: 'select',
        Values: this.locationOptions,
        Dependencies: dependencies
      });
    }

    fields.push({
      Id: QueuedMessageConditionField.LocationCountry,
      Name: locationCountry,
      ValueType: 'select',
      Values: countries
    });

    return fields;
  }

  private async getConditionOperators(): Promise<T4ConditionEditorOption[]> {
    const [equals, notEquals, contains, notContains, startsWith, endsWith, greaterThan, greaterThanOrEqual, lessThan, lessThanOrEqual, between] = await Promise.all([
      this.translate('Turbo.Notifications.QueuedMessageConditionOperator.Equals', 'Equals'),
      this.translate('Turbo.Notifications.QueuedMessageConditionOperator.NotEquals', 'Not equals'),
      this.translate('Turbo.Notifications.QueuedMessageConditionOperator.Contains', 'Contains'),
      this.translate('Turbo.Notifications.QueuedMessageConditionOperator.NotContains', 'Not contains'),
      this.translate('Turbo.Notifications.QueuedMessageConditionOperator.StartsWith', 'Starts with'),
      this.translate('Turbo.Notifications.QueuedMessageConditionOperator.EndsWith', 'Ends with'),
      this.translate('Turbo.Notifications.QueuedMessageConditionOperator.GreaterThan', 'Greater than'),
      this.translate('Turbo.Notifications.QueuedMessageConditionOperator.GreaterThanOrEqual', 'Greater or equal'),
      this.translate('Turbo.Notifications.QueuedMessageConditionOperator.LessThan', 'Less than'),
      this.translate('Turbo.Notifications.QueuedMessageConditionOperator.LessThanOrEqual', 'Less or equal'),
      this.translate('Turbo.Notifications.QueuedMessageConditionOperator.Between', 'Between')
    ]);

    return [
      { Id: QueuedMessageConditionOperator.Equals, Name: equals },
      { Id: QueuedMessageConditionOperator.NotEquals, Name: notEquals },
      { Id: QueuedMessageConditionOperator.Contains, Name: contains },
      { Id: QueuedMessageConditionOperator.NotContains, Name: notContains },
      { Id: QueuedMessageConditionOperator.StartsWith, Name: startsWith },
      { Id: QueuedMessageConditionOperator.EndsWith, Name: endsWith },
      { Id: QueuedMessageConditionOperator.GreaterThan, Name: greaterThan },
      { Id: QueuedMessageConditionOperator.GreaterThanOrEqual, Name: greaterThanOrEqual },
      { Id: QueuedMessageConditionOperator.LessThan, Name: lessThan },
      { Id: QueuedMessageConditionOperator.LessThanOrEqual, Name: lessThanOrEqual },
      { Id: QueuedMessageConditionOperator.Between, Name: between }
    ];
  }

  private getCountryOptions(): T4ConditionEditorOption[] {
    return [
      { Id: 1, Name: 'Sweden', Value: 'Sweden' },
      { Id: 2, Name: 'Denmark', Value: 'Denmark' },
      { Id: 3, Name: 'Norway', Value: 'Norway' },
      { Id: 4, Name: 'Germany', Value: 'Germany' }
    ];
  }

  private getRentalStatusOptions(): T4ConditionEditorOption[] {
    return [
      { Id: 0, Name: 'Reserved', Value: '0' },
      { Id: 1, Name: 'Canceled', Value: '1' },
      { Id: 50, Name: 'Reserved and bound', Value: '50' },
      { Id: 100, Name: 'Active', Value: '100' },
      { Id: 150, Name: 'Late', Value: '150' },
      { Id: 200, Name: 'Returned', Value: '200' },
      { Id: -2, Name: 'Service', Value: '-2' },
      { Id: -1, Name: 'New', Value: '-1' }
    ];
  }

  public createEmptySetting(): QueuedMessageSettingInfo {
    return {
      Id: undefined,
      ContextId: undefined,
      Name: '',
      Category: QueuedMessageCategory.Marketing,
      Method: QueuedMessageMethod.Notification,
      Priority: QueuedMessagePriority.Normal,
      IsActive: true,
      Title: '',
      Message: '',
      ActionText: '',
      ActionLink: '',
      DelayPeriodInMinutes: 0,
      MaximumSendCount: 1,
      RepeatIntervalInMinutes: undefined,
      UseFallback: false,
      FallbackMethod: QueuedMessageMethod.Sms,
      SendFallbackImmediatelyOnFailure: false,
      IsOneTime: true,
      ScheduledAt: undefined,
      OneTimeQueuedAt: undefined,
      StartDate: undefined,
      EndDate: undefined,
      Conditions: []
    };
  }

  public onMaximumSendCountChanged(): void {
    if (!this.setting) {
      return;
    }

    if (!this.setting.MaximumSendCount ||
      this.setting.MaximumSendCount < 1) {
      this.setting.MaximumSendCount = 1;
    }

    if (this.setting.MaximumSendCount === 1) {
      this.setting.RepeatIntervalInMinutes = undefined;
    }
  }

  public isRepeatEnabled(): boolean {
    return (this.setting?.MaximumSendCount ?? 1) > 1;
  }

  public isValidActionLink(): boolean {
    const value = this.setting?.ActionLink?.trim();

    if (!value) {
      return true;
    }

    try {
      const url = new URL(value);
      return (url.protocol === 'http:' || url.protocol === 'https:');
    } catch {
      return false;
    }
  }

  public openPreviewLink(): void {
    const value = this.setting?.ActionLink?.trim();

    if (!value || !this.isValidActionLink()) {
      return;
    }

    window.open(value, '_blank', 'noopener,noreferrer');
  }

  public async save(): Promise<void> {
    if (!this.setting) {
      return;
    }

    if (!this.setting.Name?.trim() || !this.setting.Title?.trim() || !this.setting.Message?.trim()) {
      await this.UI.alert('Turbo.NotificationSettings.headValidationError', 'Missing required information', 'Turbo.NotificationSettings.msgRequiredFields', 'Name, title and message are required.');
      return;
    }


    if (this.setting.ActionLink?.trim() && !this.setting.ActionText?.trim()) {
      await this.UI.alert('Turbo.NotificationSettings.headActionTextRequired', 'Action text required', 'Turbo.NotificationSettings.msgActionTextRequired', 'Action text is required when an action link is provided.');
      return;
    }

    if (this.isRepeatEnabled() && (!this.setting.RepeatIntervalInMinutes || this.setting.RepeatIntervalInMinutes < 1)) {
      await this.UI.alert('Turbo.NotificationSettings.headRepeatIntervalRequired', 'Repeat interval required', 'Turbo.NotificationSettings.msgRepeatIntervalRequired', 'Enter a repeat interval when the maximum send count is greater than one.');
      return;
    }

    if (this.setting.IsOneTime && (!this.scheduledDateSelected || !this.scheduledTime)) {
      await this.UI.alert('Turbo.NotificationSettings.headScheduleRequired', 'Schedule required', 'Turbo.NotificationSettings.msgScheduleRequired', 'Select both a schedule date and time for a one-time notification.');
      return;
    }

    if (!this.setting.IsOneTime && (!this.setting.StartDate || !this.setting.EndDate)) {
      await this.UI.alert('Turbo.NotificationSettings.headPeriodRequired', 'Period required', 'Turbo.NotificationSettings.msgPeriodRequired', 'Select both a start date and an end date.');
      return;
    }

    if (!this.setting.IsOneTime && this.setting.EndDate.isBefore(this.setting.StartDate, 'day')) {
      await this.UI.alert('Turbo.NotificationSettings.headInvalidPeriod', 'Invalid period', 'Turbo.NotificationSettings.msgInvalidPeriod', 'End date must be the same as or later than the start date.');
      return;
    }

    if (this.setting.IsOneTime) {
      this.setting.StartDate = undefined;
      this.setting.EndDate = undefined;
    }
    else {
      this.setting.ScheduledAt = undefined;
    }

    this.isSaving = true;
    try {
      const result = await this.Api.post<string>("/Notification/SaveNotificationSetting", this.setting);
      if (!result) {
        await this.UI.alert('Turbo.NotificationSettings.headSaveError', 'Could not save the changes', 'Turbo.NotificationSettings.msgChangeFailedIssues', 'There was an error saving the notification settings. Please try again later.');
        return;
      }

      this.router.navigate(['/Campaigns/Campaignlist']);
    } finally {
      this.isSaving = false;
    }
  }

  public cancel() {
    this.router.navigate(['/Campaigns/Campaignlist']);
  }

  public getCategoryName(id: number): any {
    return this.categories.find(item => item.Id === id)?.Name ?? '';
  }

  public getMethodName(id?: number): any {
    if (id === null || id === undefined) {
      return '';
    }

    return this.methods.find(item => item.Id === id)?.Name ?? '';
  }

  public getPriorityName(id: number): any {
    return this.priorities.find(item => item.Id === id)?.Name ?? '';
  }
}
